import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { HubspotContactProperty } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';

export interface ScannedPropertyRental {
  rentAmount: number;
  rentFrequency: string;
  propertyAgent: string;
  rentPeriodStart: string;
  rentPeriodEnd: string;
  documentId: string;
  ownerEmail: string;
}

export interface ScannedPropertyPurchase {
  purchasePrice: number;
  currency: string;
  contractDate: string;
  settlementDate: string;
  depositAmount: number | string;
  purchaserNames: string | null;
  vendorNames: string | null;
  documentId: string;
}

export interface ScannedPropertyMortgage {
  documentId: string;
  [key: string]: any;
}

export interface ScannedProperty {
  ownerEmail: string;
  address: string;
  suburb: string;
  stateOrRegion: string | null;
  postcode: string | null;
  country: string | null;
  mortgage: ScannedPropertyMortgage[];
  purchase: ScannedPropertyPurchase[];
  rental: ScannedPropertyRental[];
}

export interface PropertyOwnershipClient {
  id: string;
  email: string;
  lastName: string;
  firstName: string;
  phoneNumber: string | null;
  sourceSystem: string;
  sourceUserId: string;
  initialService: string;
  hubspotId: string | null;
  meta: { fullname: string | null };
}

export interface PropertyOwnershipEntry {
  id: string;
  clientId: string;
  client: PropertyOwnershipClient;
  ownershipPercent: string;
  acquiredOn: string | null;
  disposedOn: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  propertyId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface PropertyOwnershipDetails {
  country: string | null;
  ownership: string;
  ownerships: PropertyOwnershipEntry[];
  property_use: string;
  property_rent?: number | null;
  property_owner: string;
  property_value: number | null;
  property_address: string;
  property_address_object: Record<string, string> | null;
  property_rent_frequency?: string | null;
  mortgage_lender?: string | null;
  mortgage_interest?: number | null;
  mortgage_loan_term?: number | null;
  mortgage_loan_limit?: number | null;
  mortgage_interest_type?: string | null;
  is_mortgage_on_property?: boolean;
  repayment_sum?: number | null;
}

export interface PropertyOwnership {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  sourcePropertyId: string;
  propertyLocation: string;
  propertyDetails: PropertyOwnershipDetails;
  hubspotCustomObjectId: string | null;
  hubspotStatus: string | null;
  propertyType: string | null;
  deletedAt: string | null;
  /** Canonical relation. Prefer this over the legacy JSON ownership stamp. */
  ownerships?: PropertyOwnershipEntry[];
  /** Assessments this property is currently linked to (join rows). */
  assessmentIds?: string[];
}

export interface PropertyDocumentsResponse {
  fromDocuments: ScannedProperty[];
  fromPropertyOwnerships: PropertyOwnership[];
}

export const contactsApi = createApi({
  reducerPath: 'contactsApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    createInfynityContact: builder.mutation<
      ApiResponse<any>,
      {
        body: any;
        createHubspotContact?: boolean;
      }
    >({
      query: ({ body, createHubspotContact = false }) => {
        return {
          url: '/v1/infynity/individuals',
          method: 'POST',
          params: { createHubspotContact },
          body,
        };
      },
    }),
    createInfynityIndividualContact: builder.mutation<
      ApiResponse<any>,
      {
        body: any;
      }
    >({
      query: ({ body }) => {
        return {
          url: '/v1/infynity/individual',
          method: 'POST',
          body,
        };
      },
    }),

    getContactDetailsByEmail: builder.query<
      ApiResponse<any>,
      { email: string }
    >({
      query: ({ email }) => {
        return {
          url: '/contacts/details',
          params: { email },
        };
      },
    }),
    getPreLodgementChecklist: builder.query<
      ApiResponse<{
        count: number;
        next: string | null;
        previous: string | null;
        results: any[];
      }>,
      {
        email?: string;
        page?: number;
        page_size?: number;
        search?: string;
        lender?: string;
        interest_rate_min?: number;
        interest_rate_max?: number;
        location?: 'australia' | 'overseas';
        has_mortgage?: boolean;
      } | void
    >({
      query: (arg) => ({
        url: '/odin-admin/prelodgement-checklist/list',
        params: arg ?? undefined,
      }),
    }),
    updateHemDetails: builder.mutation<
      ApiResponse<any>,
      { email: string; factFindData: HubspotContactProperty }
    >({
      query: ({ email, factFindData }) => {
        return {
          url: `/v1/crm/hubspot/contact/update-factfind-details`,
          method: 'POST',
          body: {
            email,
            factFindData,
          },
        };
      },
    }),
    updateProperty: builder.mutation<
      ApiResponse<any>,
      {
        id: string;
        propertyDetails?: Record<string, any>;
        propertyOwnership?: {
          clientId: string;
          ownershipPercent: number;
        }[];
      }
    >({
      query: ({ id, propertyDetails, propertyOwnership }) => ({
        url: `/v1/properties/${id}`,
        method: 'PATCH',
        body: {
          ...(propertyDetails ? { propertyDetails } : {}),
          ...(propertyOwnership ? { propertyOwnership } : {}),
        },
      }),
    }),
    permanentlyDeleteProperty: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/properties/${id}`,
        method: 'DELETE',
      }),
    }),
    getPropertyDocuments: builder.query<
      ApiResponse<PropertyDocumentsResponse>,
      { emails: string[] }
    >({
      query: ({ emails }) => ({
        url: '/documents/properties',
        params: { emails: emails.join(',') },
      }),
    }),
  }),
});

export const {
  useCreateInfynityContactMutation,
  useCreateInfynityIndividualContactMutation,
  useGetContactDetailsByEmailQuery,
  useGetPreLodgementChecklistQuery,
  useUpdateHemDetailsMutation,
  useUpdatePropertyMutation,
  usePermanentlyDeletePropertyMutation,
  useGetPropertyDocumentsQuery,
} = contactsApi;
